import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Form,
  Button,
  Spin,
  Col,
  Modal,
  Row,
  DatePicker,
  Input,
  message,
} from 'antd';
import { onError } from 'utils/handlers';
import FormSubmitControls from 'components/FormSubmitControls';
import Table from 'components/Table';
// import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import { DATETIME_FRONTEND_FORMAT, DATE_FRONTEND_FORMAT } from 'utils/formatValues';
import {
  getObligacionesFiscalesContribuyente,
  patchObligacionFiscalContribuyente,
  postObligacionFiscalContribuyente,
} from 'api/empresas/obligacionesFiscalesContribuyente';
import { getObligacionesFiscales } from 'api/empresas/obligacionesFiscales';
import { getEstatusDeObigacionesFiscales } from 'api/empresas';
import {
  getContentTypesPadrones,
  getCatalogoDePadrones,
  getContenTypePorTipoDePadron,
  getTipoDePadronPorContenType,
} from 'api/catalogos';
import BusquedaAvanzadaPadrones from 'views/Recaudacion/Caja/BusquedaAvanzadaPadrones';
import { useSelector } from 'react-redux';
import { InputSearchPro } from 'components/InputSearch';
import { toMoment } from 'utils/normalizers';
import { getPadronesMap } from 'api/recaudacion/utils';

export const model = 'obligacionfiscaldecontribuyente';
export const catalogsModels = ['obligacionfiscal', 'estatusdeobligacionfiscal', 'padron'];
export const permissionConfig = {
  title: 'Obligaciones Fiscales',
  model,
  catalogs: catalogsModels,
};
const ObligacionesFiscalesContribuyente = ({
  contribuyente,
  permission,
  contribuyenteRFC,
  tipoDePadron,
  padron,
  callback,
  fetchCallback,
  mutable,
}) => {
  // eslint-disable-next-line max-len
  const [visibleBusquedaPadronesDependientes, setVisibleBusquedaPadronesDependientes] = useState(false);
  const [form] = Form.useForm();
  const estatus_de_obligaciones_fiscal = Form.useWatch('estatus_de_obligaciones_fiscal', form);
  const [tipoDePadronSelected, setTipoDePadronSelected] = useState(tipoDePadron || 8);
  const padrones = useSelector(({ catalogs }) => catalogs.padrones)
    .filter((e) => ![1, 2, 3, 4, 5, 13, 15, 16, 17].includes(e.id));
  const descripcionPadron = padrones.find((e) => e.id === tipoDePadronSelected)?.descripcion || '';
  const [data, setData] = useState({ page: 1, results: [] });
  const [padronFound, setPadronFound] = useState(padron);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [obligaciones, setObligaciones] = useState([]);
  const [estatusDeObligaciones, setEstatusObligaciones] = useState([]);
  const [visibleBusquedaPadrones, setVisibleBusquedaPadrones] = useState(false);
  const [disableSearch, setDisableSearch] = useState(false);
  const [padronDependienteFound, setPadronDependienteFound] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchData = async (page = 1, q = null) => {
    setLoading(true);
    setCurrentPage(page);
    const params = { contribuyente, page, ...q };
    if (padron) {
      params.clave_cuenta_de_padron_obligado = padron.clave;
    }
    const _data = await getObligacionesFiscalesContribuyente(params);
    const _padrones = await Promise.all(_data.results.map((ob) => getPadronesMap
      ?.[getTipoDePadronPorContenType(ob.content_type_padron)]({ id: ob.object_id_padron })));
    _padrones.forEach(([p], idx) => {
      _data.results[idx].establecimiento = p?.empresa?.nombre_comercial;
    });
    _data.page = page;
    setData(_data);
    if (fetchCallback) fetchCallback(_data);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      const [
        _actividades,
        _estatus,
      ] = await Promise.all([
        getObligacionesFiscales(),
        getEstatusDeObigacionesFiscales(),
        getContentTypesPadrones(),
        getCatalogoDePadrones(),
      ]);
      await fetchData();
      if (mounted) {
        setObligaciones(padron
          ? _actividades.filter((o) => o.tipo_padron === tipoDePadron) : _actividades);
        setEstatusObligaciones(_estatus);
      }
    };
    fetch();
    return () => { mounted = false; };
  }, []);

  const fillValuesPadron = () => {
    if (padronFound) {
      form.setFieldsValue({
        object_id_padron: padronFound.id,
        clave_cuenta_de_padron_obligado: padronFound?.clave,
        contribuyente_rfc: padronFound?.empresa?.contribuyente?.rfc,
      });
    }
  };
  const fillValuesPadronDependiente = () => {
    if (padronDependienteFound) {
      form.setFieldsValue({
        object_id_padron_padre_dependiente: padronDependienteFound.id,
      });
    }
  };

  const onCancel = () => {
    form.resetFields();
    setVisible(false);
    setSelectedRowKeys([]);
    setTipoDePadronSelected(tipoDePadron || 8);
    setPadronFound(padron);
    fillValuesPadron();
    setPadronDependienteFound();
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      values.contribuyente = contribuyente;
      values.content_type_padron = getContenTypePorTipoDePadron(tipoDePadronSelected);
      delete values.content_padre;
      // eslint-disable-next-line max-len
      if (padron && !key) {
        values.object_id_padron = padron.id;
      }
      const saved = await (key
        ? patchObligacionFiscalContribuyente(key, values)
        : postObligacionFiscalContribuyente(values));
      if (saved) {
        const descripcionObligacion = obligaciones
          .find((o) => o.id === values.obligacion_fiscal)?.descripcion;
        const movementType = key ? 'editada' : 'registrada';
        const padronToStr = ` ${saved.id} - ${padronFound.clave} - ${padronFound.empresa?.nombre_comercial
          || padronFound.empresa?.contribuyente?.nombre_completo}`;
        const detailItems = [
          `Fecha inicio: ${values.fecha_inicio.format()}`,
          values.fecha_fin ? `Fecha fin: ${values.fecha_fin.format() || 'N/A'}` : null,
          `Estatus: ${estatusDeObligaciones.find((e) => e.id === values.estatus_de_obligaciones_fiscal)?.descripcion}`,
          values.observaciones ? `Observaciones: ${values.observaciones}` : null,
        ].filter((e) => e);
        const detail = detailItems.join(', ');
        saved.message = `Obligación ${descripcionObligacion} ${padron ? '' : `para ${descripcionPadron} ${padronToStr}`} [${movementType}]: ${detail}`;
        onCancel();
        await fetchData();
        if (callback) callback(saved);
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const retrievePadron = async (id, tipoPadron = tipoDePadronSelected) => {
    const _padrones = await getPadronesMap[tipoPadron]({ id });
    if (_padrones.length || _padrones.count) {
      if ((_padrones.length || _padrones.count) === 1) {
        const _padronFound = _padrones.results?.[0] || _padrones[0];
        setPadronFound(_padronFound);
        form.setFieldsValue({ object_id_padron: _padronFound.id });
      }
    }
  };

  const retrievePadronDependiente = async (id, tipoPadron = tipoDePadronSelected) => {
    const _padrones = await getPadronesMap[tipoPadron]({ id });
    if (_padrones.length || _padrones.count) {
      if ((_padrones.length || _padrones.count) === 1) {
        const _padronFound = _padrones.results?.[0] || _padrones[0];
        setPadronDependienteFound(_padronFound);
        form.setFieldsValue({ object_id_padron_padre_dependiente: _padronFound.id });
      }
    }
  };

  const searchPadron = async () => {
    setLoading(true);
    const q = form.getFieldValue('q');
    if (q) {
      const _padrones = await getPadronesMap[tipoDePadronSelected]({ q });
      if (_padrones.length || _padrones.count) {
        if ((_padrones.length || _padrones.count) === 1) {
          const _padronFound = _padrones.results?.[0] || _padrones[0];
          setPadronFound(_padronFound);
          form.setFieldsValue({ object_id_padron: _padronFound.id });
        } else {
          message.warn('Ingrese una búsqueda más específica');
        }
      } else {
        message.warn('No se encontro el padrón');
      }
    }
    setLoading(false);
  };
  const searchPadronDependiente = async () => {
    setLoading(true);
    const q = form.getFieldValue('q_dependiente');
    if (q) {
      const _padrones = await getPadronesMap[tipoDePadronSelected]({ q });
      if (_padrones.length || _padrones.count) {
        if ((_padrones.length || _padrones.count) === 1) {
          const _padronFound = _padrones.results?.[0] || _padrones[0];
          setPadronDependienteFound(_padronFound);
          form.setFieldsValue({ object_id_padron_padre_dependiente: _padronFound.id });
        } else {
          message.warn('Ingrese una búsqueda más específica');
        }
      } else {
        message.warn('No se encontro el padrón');
      }
    }
    setLoading(false);
  };
  const onClickEdit = async () => {
    const [key] = selectedRowKeys;
    const match = data.results.find((e) => e.id === key);
    setTipoDePadronSelected(getTipoDePadronPorContenType(match.content_type_padron));
    await retrievePadron(
      match.object_id_padron,
      getTipoDePadronPorContenType(match.content_type_padron),
    );

    if (match.object_id_padron_padre_dependiente) {
      await retrievePadronDependiente(
        match.object_id_padron_padre_dependiente,
        getTipoDePadronPorContenType(match.content_type_padron),
      );
    }
    // eslint-disable-next-line max-len
    form.setFieldsValue({ tipo_de_padron: getTipoDePadronPorContenType(match.content_type_padron) });
    setVisible(true);
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const columns = [
    {
      titleText: 'Tipo de Padrón',
      key: 'content_type_padron',
      dataIndex: 'content_type_padron',
      width: 200,
      render: (val) => padrones
        ?.find((e) => e.id === getTipoDePadronPorContenType(val))?.descripcion,
    },
    {
      titleText: 'Clave de Registro Estatal',
      key: 'clave_cuenta_de_padron_obligado',
      dataIndex: 'clave_cuenta_de_padron_obligado',
      width: 200,
    },
    {
      titleText: 'Nombre del Establecimiento',
      key: 'establecimiento',
      dataIndex: 'establecimiento',
      width: 200,
    },
    {
      titleText: 'Obligación Fiscal',
      key: 'obligacion_fiscal',
      dataIndex: 'obligacion_fiscal',
      width: 300,
      render: (val) => obligaciones.find((e) => e.id === val)?.descripcion,
    },
    {
      titleText: 'Fecha Inicio',
      key: 'fecha_inicio',
      dataIndex: 'fecha_inicio',
      width: 200,
      render: (val) => val?.format(),
    },
    {
      titleText: 'Fecha Fin',
      key: 'fecha_fin',
      dataIndex: 'fecha_fin',
      width: 200,
      render: (val) => val?.format(),
    },

    {
      titleText: 'Estado',
      key: 'estatus_de_obligaciones_fiscal',
      dataIndex: 'estatus_de_obligaciones_fiscal',
      width: 200,
      render: (val) => estatusDeObligaciones.find((e) => e.id === val)?.descripcion,
    },
    {
      titleText: 'Clave Padre',
      key: 'clave_padre',
      dataIndex: 'clave_padre',
      width: 200,
    },
  ].filter((e) => e);

  const resetAndValidate = () => {
    setPadronFound();
    form.resetFields(['q']);
    form.validateFields(['q']);
  };

  const resetAndValidateDependiente = () => {
    setPadronDependienteFound();
    form.resetFields(['q_dependiente']);
    form.validateFields(['q_dependiente']);
  };

  const rules = {
    required: [{ required: true }],
  };

  const onChangeObligacion = (value) => {
    if (value) {
      const match = obligaciones.find((e) => e.id === value);
      setTipoDePadronSelected(match.tipo_padron);
      form.setFieldsValue({ tipo_de_padron: match.tipo_padron });
      form.setFieldsValue({ content_padre: match.tipo_padron });
      setDisableSearch(true);
      if (!padronFound) {
        resetAndValidate();
      }
    }
  };

  useEffect(() => {
    fillValuesPadron(padronFound);
  }, [padronFound]);

  useEffect(() => {
    fillValuesPadronDependiente(padronDependienteFound);
  }, [padronDependienteFound]);

  return (
    <>
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data.results}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={mutable ? {
            onClickAdd: padron || !callback ? onClickAdd : null,
            onClickEdit,
          } : { onClickExpand: onClickEdit }}
          allowExpand
          allowSort
          allowSearch
          rowKey="id"
          permission={permission}
          pagination={false}
          paginationCallback={fetchData}
          count={data?.count}
          currentPage={currentPage}
          hasAllViewPermission
        />
      </Spin>
      <Modal
        open={visible}
        closable={false}
        footer={null}
        title={(
          <FormSubmitControls
            label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Obligación Fiscal`}
            onFinish={mutable ? onFinish : null}
            onCancel={onCancel}
            loading={loading}
          />
        )}
        keyboard={!loading}
        maskClosable={!loading}
        onCancel={onCancel}
        forceRender
      >
        <Spin tip="Cargando..." spinning={loading}>
          <Col span={24}>
            <Form
              form={form}
              layout="vertical"
              onFinish={() => onFinish()}
              initialValues={padron ? {
                estatus_de_obligaciones_fiscal: 1,
                fecha_inicio: toMoment(new Date()),
              } : { fecha_de_inicio_de_dependiente: toMoment(new Date()) }}
            >
              <Row gutter={10}>
                <Col span={24}>
                  <Form.Item
                    name="obligacion_fiscal"
                    label="Obligación Fiscal"
                    rules={rules.required}
                  >
                    <Select
                      dataSource={obligaciones}
                      onChange={onChangeObligacion}
                      allowClear
                      disabled={!mutable}
                    />
                  </Form.Item>
                </Col>
                {!padronFound && (
                  <Col span={24}>
                    <InputSearchPro
                      label={`Búscar ${descripcionPadron}`}
                      name="q"
                      onClickAdvanced={() => setVisibleBusquedaPadrones(true)}
                      breakPoints={{
                        md: 24,
                      }}
                      disabled={!disableSearch || !mutable}
                      rules={rules.required}
                      prefix={{
                        rules: rules.required,
                        disabled: true,
                        dataSource: padrones,
                        label: 'Padrón',
                        name: 'tipo_de_padron',
                        onChange: setTipoDePadronSelected,
                        style: { width: 150 },
                        allowClear: false,
                      }}
                      inputProps={{
                        onPressEnter: () => searchPadron(),
                        onChange: ({ target: { value } }) => {
                          if (!value) {
                            resetAndValidate();
                          }
                        },
                      }}
                      resultProps={{
                        name: 'object_id_padron',
                        label: padrones.find((e) => e.id === tipoDePadronSelected)?.descripcion,
                        rules: rules.required,
                        dataSource: padronFound,
                        render: (_padron) => `
                          ${_padron?.clave} -
                          ${_padron?.empresa?.nombre_comercial || _padron?.empresa?.contribuyente?.nombre_completo}
                        `,
                        keyLabelRender: false,
                        onClear: resetAndValidate,
                        breakPoints: {
                          md: 24,
                        },
                      }}
                    />
                  </Col>
                )}
                {padronFound && (
                  <>
                    <Col span={12}>
                      <Form.Item
                        name="clave_cuenta_de_padron_obligado"
                        label="Clave"
                      >
                        <Input allowClear disabled />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="contribuyente_rfc"
                        label="RFC"
                      >
                        <Input allowClear disabled />
                      </Form.Item>
                    </Col>
                    <Form.Item
                      name="object_id_padron"
                      hidden
                    >
                      <Input allowClear disabled />
                    </Form.Item>
                  </>
                )}
                <Col span={24}>
                  <Form.Item
                    name="fecha_inicio"
                    label="Fecha de Inicio"
                    rules={rules.required}
                  >
                    <DatePicker
                      placeholder=""
                      format={DATE_FRONTEND_FORMAT}
                      showTime
                      disabled={!mutable}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="fecha_fin"
                    label="Fecha Fin"
                    rules={[2, 3].includes(estatus_de_obligaciones_fiscal) ? rules.required : []}
                  >
                    <DatePicker
                      placeholder=""
                      format={DATE_FRONTEND_FORMAT}
                      showTime
                      disabled={!mutable}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="estatus_de_obligaciones_fiscal"
                    label="Estado"
                    rules={rules.required}
                  >
                    <Select
                      disabled={(!!padron && !!fetchCallback) || !mutable}
                      dataSource={estatusDeObligaciones}
                      allowClear
                    />
                  </Form.Item>
                </Col>
                {estatus_de_obligaciones_fiscal === 4 && (
                  <>
                    <Col span={24}>
                      <InputSearchPro
                        label={`${descripcionPadron} dependiente`}
                        name="q_dependiente"
                        onClickAdvanced={() => setVisibleBusquedaPadronesDependientes(true)}
                        breakPoints={{
                          md: 24,
                        }}
                        disabled={!mutable}
                        rules={rules.required}
                        prefix={{
                          rules: rules.required,
                          disabled: true,
                          dataSource: padrones,
                          label: 'Padrón Padre',
                          name: 'tipo_de_padron',
                          onChange: setTipoDePadronSelected,
                          style: { width: 150 },
                          allowClear: false,
                        }}
                        inputProps={{
                          onPressEnter: () => searchPadronDependiente(),
                          onChange: ({ target: { value } }) => {
                            if (!value) {
                              resetAndValidateDependiente();
                            }
                          },
                        }}
                        resultProps={{
                          name: 'object_id_padron_padre_dependiente',
                          label: `${padrones.find((e) => e.id === tipoDePadronSelected)
                            ?.descripcion} Padre`,
                          rules: rules.required,
                          dataSource: padronDependienteFound,
                          render: (_padron) => `
                          ${_padron?.clave} -
                          ${_padron?.empresa?.nombre_comercial || _padron?.empresa?.contribuyente?.nombre_completo}
                        `,
                          keyLabelRender: false,
                          onClear: resetAndValidateDependiente,
                          breakPoints: {
                            md: 24,
                          },
                        }}
                      />
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        name="fecha_de_inicio_de_dependiente"
                        label="Fecha de inicio de dependiente"
                      >
                        <DatePicker
                          placeholder=""
                          format={DATETIME_FRONTEND_FORMAT}
                          showTime
                          rules={rules?.required}
                          disabled={!!fetchCallback || !mutable}
                        />
                      </Form.Item>
                    </Col>
                  </>
                )}

                <Col span={24}>
                  <Form.Item
                    name="observaciones"
                    label="Observaciones"
                    rules={[2, 3].includes(estatus_de_obligaciones_fiscal) ? rules.required : []}
                  >
                    <Input.TextArea autoSize={{ minRows: 2, maxRows: 3 }} disabled={!mutable} />
                  </Form.Item>
                </Col>
                {!!selectedRowKeys.length && !!padron && (
                  <Col span={24}>
                    <Form.Item
                      name="fecha_ultimo_estado"
                      label="Fecha Último Estado"
                    >
                      <DatePicker
                        placeholder=""
                        format={DATETIME_FRONTEND_FORMAT}
                        showTime
                        disabled={!!fetchCallback || !mutable}
                      />
                    </Form.Item>
                  </Col>
                )}
              </Row>
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Form>
          </Col>
        </Spin>
      </Modal>
      <BusquedaAvanzadaPadrones
        title="Búsqueda Avanzada de Padrón"
        visible={visibleBusquedaPadrones}
        setVisible={setVisibleBusquedaPadrones}
        callback={(record) => setPadronFound(record)}
        tipoDePadron={tipoDePadronSelected}
        contribuyenteRFC={contribuyenteRFC}
        visibleRFC={false}
        visibleCURP={false}
      />
      <BusquedaAvanzadaPadrones
        title="Búsqueda Avanzada de Padrón"
        visible={visibleBusquedaPadronesDependientes}
        setVisible={setVisibleBusquedaPadronesDependientes}
        callback={(record) => setPadronDependienteFound(record)}
        tipoDePadron={tipoDePadronSelected}
        contribuyenteRFC={contribuyenteRFC}
        visibleRFC={false}
        visibleCURP={false}
      />
    </>
  );
};

ObligacionesFiscalesContribuyente.propTypes = {
  contribuyente: PropTypes.number,
  permission: PropTypes.shape({
    add: PropTypes.bool.isRequired,
    change: PropTypes.bool.isRequired,
    delete: PropTypes.bool.isRequired,
    export: PropTypes.bool.isRequired,
    import: PropTypes.bool.isRequired,
    view: PropTypes.bool.isRequired,
  }).isRequired,
  contribuyenteRFC: PropTypes.string,
  tipoDePadron: PropTypes.number,
  callback: PropTypes.func,
  fetchCallback: PropTypes.func,
  padron: PropTypes.shape({ id: PropTypes.number, clave: PropTypes.string }),
  mutable: PropTypes.bool,
};

ObligacionesFiscalesContribuyente.defaultProps = {
  contribuyente: null,
  contribuyenteRFC: null,
  tipoDePadron: null,
  callback: null,
  fetchCallback: null,
  padron: null,
  mutable: true,
};

export default ObligacionesFiscalesContribuyente;
